import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { IoMdClose } from "react-icons/io";
import type { StatusHistoryRow } from "../../utils/projectStatusTimeline";

const JUSTIFICATION_MAX_CHARS = 200;

function summarizeJustification(text?: string): string {
  const trimmed = text?.trim() ?? "";
  if (!trimmed) return "—";
  if (trimmed.length <= JUSTIFICATION_MAX_CHARS) return trimmed;
  return `${trimmed.slice(0, JUSTIFICATION_MAX_CHARS).trimEnd()}…`;
}

interface StatusHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  rows: StatusHistoryRow[];
}

function JustificationCell({ text }: { text?: string }) {
  const full = text?.trim() ?? "";
  const summarized = summarizeJustification(full);

  if (!full) {
    return (
      <Typography variant="body2" color="text.secondary">
        {summarized}
      </Typography>
    );
  }

  return (
    <Tooltip title={full} placement="top">
      <Typography
        variant="body2"
        style={{
          display: "-webkit-box",
          WebkitBoxOrient: "vertical",
          WebkitLineClamp: 3,
          overflow: "hidden",
          wordBreak: "break-word",
        }}
      >
        {summarized}
      </Typography>
    </Tooltip>
  );
}

const StatusHistoryModal = ({
  isOpen,
  onClose,
  rows,
}: StatusHistoryModalProps) => {
  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      aria-labelledby="status-history-title"
    >
      <DialogTitle
        id="status-history-title"
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: 4,
          py: 2.5,
          color: "#1361A4",
        }}
      >
        <Typography
          component="span"
          variant="h6"
          color="#1361A4"
          fontWeight="bold"
        >
          Histórico de status
        </Typography>
        <IconButton
          aria-label="Fechar histórico"
          onClick={onClose}
          sx={{ color: "#666" }}
        >
          <IoMdClose size={24} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 4, py: 2 }}>
        <Box sx={{ overflowX: "auto" }}>
          <Table size="small" aria-label="Histórico de transições de status">
            <TableHead>
              <TableRow>
                <TableCell>Status</TableCell>
                <TableCell>Entrou em</TableCell>
                <TableCell>Saiu em</TableCell>
                <TableCell>Duração</TableCell>
                <TableCell>Ocorrência</TableCell>
                <TableCell>Justificativa</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, index) => (
                <TableRow
                  key={`${row.statusValue}-${row.occurrence}-${index}`}
                  data-retorno={row.isReturn ? "true" : "false"}
                  sx={{
                    bgcolor: row.isReturn
                      ? "rgba(230, 126, 34, 0.06)"
                      : undefined,
                  }}
                >
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      {row.statusLabel}
                      {row.isReturn && (
                        <Chip
                          label="retorno"
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: 11,
                            bgcolor: "#FFF3E0",
                            color: "#E67E22",
                          }}
                        />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>{row.enteredAtLabel}</TableCell>
                  <TableCell>{row.exitedAtLabel}</TableCell>
                  <TableCell>{row.durationLabel}</TableCell>
                  <TableCell>{row.occurrenceLabel}</TableCell>
                  <TableCell sx={{ maxWidth: 280, minWidth: 160 }}>
                    <JustificationCell text={row.justification} />
                  </TableCell>
                </TableRow>
              ))}
              {rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6}>
                    Nenhuma transição de status registrada.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 4, py: 2 }}>
        <Button onClick={onClose}>Fechar</Button>
      </DialogActions>
    </Dialog>
  );
};

export default StatusHistoryModal;
